import { Button, Form, Modal, Popover } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';

import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import { useSaveAdditionalPax } from 'app/hooks/data/useAdditionalPax';
import { useAppSelector } from 'app/redux/hooks';
import { BABY_COT_BED_CATEGORY_ID } from 'app/typings';
import type { ExtranetRoom } from 'app/typings/packages';

import type { GuestTask } from '../TaskCenter/Legacy/Types';

import { Configuration } from './Components/Configuration';
import { Overview } from './Components/Overview';
import './ExtraGuest.scss';
import type { GuestTaskValueName, PackageToggleSwitchParam } from './Utils';
import { getExtraGuestDetail, getSumOfPerPersonExperience } from './Utils';

type ExtraGuestSettingsFormValues = {
  [packageId: number]: number;
};

export type ExtraGuestSettingsFormattedValue = {
  [valueName in GuestTaskValueName]?: number | null;
} & {
  packageId: number;
};

type ExtraGuestModalProps = {
  taskName: GuestTask;
  isOpen?: boolean;
  onCompleteTask?: () => void;
  rooms: ExtranetRoom[];
};

export const ExtraGuestModal = ({
  taskName,
  isOpen,
  onCompleteTask,
  rooms,
}: ExtraGuestModalProps) => {
  const [form] = Form.useForm();
  const { hotelId } = useContext(HotelContext);
  const { publishedPackages } = useContext(LegacyHotelContext);

  const { clearOpenTask } = useContext(TaskCenterContext);

  const { mutateAsync: saveAdditionalPax } = useSaveAdditionalPax(
    hotelId,
    taskName
  );

  const taskInformation = getExtraGuestDetail(taskName);

  const initialSwitchValues: PackageToggleSwitchParam[] = useMemo(
    () =>
      publishedPackages?.map((pkg) => ({
        packageId: pkg.id,
        isSwitchOn: pkg?.[taskInformation.valueName] !== undefined,
      })) ?? [],
    [publishedPackages, taskInformation.valueName]
  );

  const [toggleSwitches, setToggleSwitches] = useState(initialSwitchValues);

  const bedCategories = useAppSelector((state) => state.conf.bedCategories);

  const getBedCapacityById = (bedId: number) => {
    const bed = bedCategories.find((b) => b.id === bedId);

    return bed ? bed.capacity : 0;
  };

  const roomsWithCapacityGreaterThan2Adults = rooms.filter((room) => {
    const totalCapacity = room.beds.reduce((sum, bed) => {
      const bedType =
        bedCategories.find((b) => b.id === bed.categoryId)?.type || '';

      if (
        bed.categoryId &&
        bed.categoryId !== BABY_COT_BED_CATEGORY_ID &&
        !bedType.includes('kid')
      ) {
        return sum + getBedCapacityById(bed.categoryId);
      }

      return sum;
    }, 0);

    return totalCapacity > 2;
  });

  const onFinish = async (values: ExtraGuestSettingsFormValues) => {
    const formattedFilteredFormValues = Object.entries(values).reduce(
      (acc, current) => {
        const [packageId, inputValue] = current;

        const isSwitchOn = toggleSwitches.find(
          (toggleParam) => toggleParam.packageId === +packageId
        )?.isSwitchOn;

        return [
          ...acc,
          {
            packageId: +packageId,
            [taskInformation.valueName]: isSwitchOn ? inputValue : null,
          } as ExtraGuestSettingsFormattedValue,
        ];
      },
      [] as ExtraGuestSettingsFormattedValue[]
    );

    onCompleteTask?.();

    const payload = { settings: formattedFilteredFormValues };

    await saveAdditionalPax(payload);

    clearOpenTask();
    setToggleSwitches(initialSwitchValues);
  };

  const handleClose = () => {
    clearOpenTask();
    setToggleSwitches(initialSwitchValues);
  };

  const initialValues = useMemo(() => {
    return publishedPackages?.reduce((acc, pkg) => {
      const sumPrice = getSumOfPerPersonExperience(
        pkg.experiences.filter((exp) => exp.included[pkg.id])
      );

      return {
        ...acc,
        [pkg.id]: pkg[taskInformation.valueName] ?? sumPrice,
      };
    }, {} as { [packageId: number]: number });
  }, [publishedPackages, taskInformation.valueName]);

  useEffect(() => {
    if (!!publishedPackages?.length) {
      setToggleSwitches(initialSwitchValues);
      form.setFieldsValue(initialValues);
    }
  }, [form, initialValues, publishedPackages, initialSwitchValues]);

  const hasSwitchesChanged = useMemo(
    () =>
      initialSwitchValues.some(
        (toggleParam) =>
          toggleParam.isSwitchOn !==
          toggleSwitches.find(
            (pkgToggleValue) =>
              pkgToggleValue.packageId === toggleParam.packageId
          )?.isSwitchOn
      ),
    [initialSwitchValues, toggleSwitches]
  );

  const hasValueChanged = Object.entries(form?.getFieldsValue() ?? []).some(
    ([packageId, inputValue]) => inputValue !== initialValues?.[+packageId]
  );

  const isSubmitDisabled = useMemo(
    () => !hasSwitchesChanged && !hasValueChanged,
    [hasSwitchesChanged, hasValueChanged]
  );

  const renderSaveButton = () => {
    if (
      taskInformation.hasOverview &&
      roomsWithCapacityGreaterThan2Adults.length === 0 &&
      !isSubmitDisabled
    ) {
      return (
        <Popover
          content={
            <div className="extra-guest-confirmation-popin">
              <div className="popin-text">
                <p>
                  Please also contact your Account Manager to define which rooms
                  are adapted for more than 2 adults.
                </p>
              </div>
              <div className="popin-cta">
                <Button
                  id="validateDesactivationDiscountRule"
                  className="popin-yes"
                  type="primary"
                  onClick={form.submit}
                >
                  OK
                </Button>
              </div>
            </div>
          }
          placement="top"
          trigger={'click'}
        >
          <Button
            key="save"
            size="large"
            type="primary"
            htmlType="submit"
            className="saveBtn"
            disabled={isSubmitDisabled}
          >
            Save
          </Button>
        </Popover>
      );
    }

    return (
      <Button
        key="save"
        size="large"
        type="primary"
        htmlType="submit"
        onClick={form.submit}
        className="saveBtn"
        disabled={isSubmitDisabled}
      >
        Save
      </Button>
    );
  };

  return (
    <FormLegacyRenderProp
      form={form}
      onFinish={onFinish}
      initialValues={initialValues}
      preserve={false}
    >
      {(_) => (
        <Modal
          width={920}
          className="extraGuestModal"
          open={isOpen}
          title={
            <div className="extraGuestModal__header">
              <h1>{taskInformation.title}</h1>
              <p> {taskInformation.description}</p>
            </div>
          }
          onCancel={handleClose}
          wrapClassName="extraGuestModalContainer"
          centered
          footer={[
            <Button size="large" type="ghost" key="close" onClick={handleClose}>
              Close
            </Button>,
            renderSaveButton(),
          ]}
          destroyOnClose
        >
          <div className="extraGuestModal__body">
            {taskInformation.hasOverview && (
              <Overview
                rooms={roomsWithCapacityGreaterThan2Adults}
                bedCategories={bedCategories}
              />
            )}
            <Configuration
              packages={publishedPackages}
              toggleSwitches={toggleSwitches}
              setToggleSwitches={setToggleSwitches}
              taskInformation={taskInformation}
            />
          </div>
        </Modal>
      )}
    </FormLegacyRenderProp>
  );
};
